/**
 * The signed-in session's org, resolved at RUNTIME — the portal's copy of the
 * studio's `studio/web/src/config/orgSession.ts` pattern (see MULTI-ORG-PORTAL
 * plan). The org used to be compiled in (`VITE_WORKOS_ORG_ID` + module-name
 * env vars), which is exactly the failure class the repo CLAUDE.md warns
 * about: a session in any OTHER org still aimed at MineTwin's databases and
 * collected entirely correct 403s (`docs/platform-issues-2026-07-29.md`).
 *
 * Two steps, because the JWT carries only an opaque `org_...` id:
 *   1. decode the `org_id` claim (unverified — the server checks signatures),
 *   2. ask `{stamp}-platform`'s `get_org_info` procedure for the slug that
 *      every module name derives from.
 *
 * `{stamp}-platform`, never the house `turbotwin-platform`: stamps are
 * co-hosted, and a stamp org has no row in the house registry — the house
 * answers 200 `{"success":false,"error":"Organization not found"}`.
 *
 * Failure policy is FAIL LOUD: no fallback slug. OrgContext gates the
 * connected tree on resolution and renders the error naming the org.
 */

/** Which stamp's control plane answers `get_org_info`. */
export const STAMP = (import.meta.env.VITE_TURBOTWIN_STAMP as string | undefined) || 'minetwin';
/** The stamp webclient host: where the launcher files crash reports and opens
 *  the crash page. Defaults to the stamp convention. */
export const WEBCLIENT_HOST =
  (import.meta.env.VITE_TURBOTWIN_WEBCLIENT_HOST as string | undefined) || `${STAMP}.turbotwin.cloud`;

export interface OrgSession {
  /** WorkOS org id from the JWT, e.g. `org_01KWJF5AT8N0YRT219MRH1FM6T`. */
  orgId: string;
  /** Slug module names derive from, e.g. `minetwin-default`. */
  slug: string;
  /** Human name for the UI, e.g. `MineTwin`. */
  displayName: string;
}

/** Decode the `org_id` claim without verifying the signature. */
export function decodeOrgId(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as Record<string, unknown>;
    return typeof payload?.org_id === 'string' ? payload.org_id : null;
  } catch {
    return null;
  }
}

/**
 * Parse a `get_org_info` response body. Procedures return a JSON-encoded
 * string and the HTTP layer re-wraps it, so the payload is double-encoded —
 * unwrap once if the outer value is a string.
 */
export function parseOrgInfo(body: string): { slug: string; displayName: string } {
  let parsed: unknown = JSON.parse(body);
  if (typeof parsed === 'string') parsed = JSON.parse(parsed);
  const obj = parsed as { success?: boolean; error?: string; data?: Record<string, unknown> };
  if (!obj?.success) {
    throw new Error(obj?.error || 'unknown error');
  }
  const slug = obj.data?.org_name;
  if (typeof slug !== 'string' || !slug) {
    throw new Error('response missing data.org_name');
  }
  const displayName = obj.data?.display_name;
  return {
    slug,
    displayName: typeof displayName === 'string' && displayName ? displayName : slug,
  };
}

const CACHE_PREFIX = 'mt:orgSession:';

function readCache(orgId: string): OrgSession | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + orgId);
    if (!raw) return null;
    const v = JSON.parse(raw) as OrgSession;
    return v && v.orgId === orgId && typeof v.slug === 'string' && v.slug ? v : null;
  } catch {
    return null;
  }
}

function writeCache(session: OrgSession): void {
  try {
    sessionStorage.setItem(CACHE_PREFIX + session.orgId, JSON.stringify(session));
  } catch {
    // sessionStorage unavailable (private mode quota etc.) — cache is optional.
  }
}

/**
 * Resolve an org id to its session (slug + display name) via `get_org_info`,
 * with a per-tab sessionStorage cache (the webclient caches the same lookup —
 * slugs are immutable in practice, and the round-trip otherwise runs on every
 * reload). Rejects with a message naming the org id and the platform DB:
 * "Organization not found" is indistinguishable between "wrong stamp" and
 * "genuinely unprovisioned" without both.
 */
export async function resolveOrgSession(
  orgId: string,
  token: string,
  httpBaseUri: string,
): Promise<OrgSession> {
  const cached = readCache(orgId);
  if (cached) return cached;

  const platformDb = `${STAMP}-platform`;
  const url = `${httpBaseUri.replace(/\/$/, '')}/v1/database/${platformDb}/call/get_org_info`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    // Procedure args are a JSON array of positional arguments.
    body: JSON.stringify([orgId]),
  });
  const text = await resp.text();
  if (!resp.ok) throw new Error(`get_org_info HTTP ${resp.status}: ${text.slice(0, 200)}`);

  try {
    const { slug, displayName } = parseOrgInfo(text);
    const session: OrgSession = { orgId, slug, displayName };
    writeCache(session);
    return session;
  } catch (e) {
    throw new Error(
      `get_org_info(${orgId}) on ${platformDb}: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
}
